/**
 * Las fotos de un producto, administradas desde su detalle (ADM-04).
 *
 * Es el unico lugar por donde entra una foto al catalogo.
 *
 * **El orden no es decorativo.** La primera foto es la principal: la que se ve
 * en la grilla del catalogo, en el carro, en el resultado de una busqueda y
 * arriba en la ficha. Por eso se puede mover, y por eso la primera lleva una
 * marca que lo dice — sin ella, nadie tiene forma de saber cual es cual.
 *
 * Se mueve de a un lugar con dos botones, igual que los banners de la portada:
 * son pocas fotos, subir y bajar se recorren con el teclado, y arrastrar habria
 * obligado a construir toda la alternativa accesible para el mismo resultado.
 *
 * Eliminar pasa por un modal porque no tiene vuelta atras —el archivo se borra
 * del almacen, no solo la fila— y es el patron que ya usan eliminar un producto
 * y dar de baja una cuenta. Nunca el `confirm()` del navegador: bloquea la
 * pestaña entera y no se puede redactar.
 *
 * **El texto alternativo se guarda al salir del campo, no con un boton.** Con
 * seis fotos, seis botones de guardar es mucho ruido para un campo que casi
 * siempre queda vacio; salir del campo es el gesto que ya significa "terminé
 * con este". Es el mismo criterio con el que el RUT del checkout se formatea al
 * salir y no en cada tecla. Vacio no es un olvido: la ficha cae al nombre del
 * producto, que ya es un alt correcto — el campo esta para la foto que muestra
 * algo que el nombre no dice.
 */
import { useState } from 'react';

import type { ImagenProducto } from '@bandu/compartido';

import { useSubidaImagen } from '../../hooks/use-subida-imagen';
import {
  describirFotoProducto,
  eliminarFotoProducto,
  moverFotoProducto,
  subirFotoProducto,
} from '../../servicios/administracion.servicio';
import { Icono } from '../comunes/Icono';
import { Imagen } from '../comunes/Imagen';
import { Modal } from './Modal';
import { PanelAdmin } from './PanelAdmin';
import { SubidorImagen } from './SubidorImagen';

export interface GaleriaProductoAdminProps {
  productoId: number;
  nombreProducto: string;
  imagenes: ImagenProducto[];
  onCambio(imagenes: ImagenProducto[]): void;
}

export function GaleriaProductoAdmin({
  productoId,
  nombreProducto,
  imagenes,
  onCambio,
}: GaleriaProductoAdminProps) {
  const subida = useSubidaImagen();
  const [moviendo, setMoviendo] = useState(false);
  const [porEliminar, setPorEliminar] = useState<ImagenProducto | null>(null);
  const [eliminando, setEliminando] = useState(false);

  function subir(archivo: File): void {
    void subida.subir(async () => {
      const nueva = await subirFotoProducto(productoId, archivo);
      onCambio([...imagenes, nueva]);
    });
  }

  function mover(imagenId: number, direccion: 'subir' | 'bajar'): void {
    setMoviendo(true);
    void subida
      .subir(async () => {
        onCambio(await moverFotoProducto(productoId, imagenId, direccion));
      })
      .finally(() => {
        setMoviendo(false);
      });
  }

  function eliminar(): void {
    if (porEliminar === null) return;
    const id = porEliminar.id;

    setEliminando(true);
    void subida
      .subir(async () => {
        await eliminarFotoProducto(productoId, id);
        onCambio(imagenes.filter((imagen) => imagen.id !== id));
        setPorEliminar(null);
      })
      .finally(() => {
        setEliminando(false);
      });
  }

  function describir(imagenId: number, texto: string): void {
    const actual = imagenes.find((imagen) => imagen.id === imagenId);
    const limpio = texto.trim().length > 0 ? texto.trim() : null;
    // Salir del campo sin haberlo tocado no es motivo para escribir en la base.
    if (actual === undefined || (actual.textoAlternativo ?? null) === limpio) return;

    void subida.subir(async () => {
      const actualizada = await describirFotoProducto(productoId, imagenId, limpio);
      onCambio(imagenes.map((imagen) => (imagen.id === imagenId ? actualizada : imagen)));
    });
  }

  const ocupado = subida.subiendo || moviendo;

  return (
    <PanelAdmin
      titulo="Fotos"
      bajada="La primera es la que se ve en el catálogo, y el texto de abajo es lo que se lee si la foto no carga."
    >
      {subida.falla !== null ? (
        <p className="acceso-error" role="alert">
          <Icono nombre="alerta" />
          <span>{subida.falla}</span>
        </p>
      ) : null}

      {imagenes.length > 0 ? (
        <ul className="galeria-admin">
          {imagenes.map((imagen, indice) => (
            <li key={imagen.id}>
              <div className="galeria-admin-foto">
                <Imagen
                  src={imagen.url}
                  alt={imagen.textoAlternativo ?? nombreProducto}
                  ancho={imagen.ancho}
                  alto={imagen.alto}
                />
                {/* Sobre la foto y no debajo: debajo empujaba solo la primera
                    celda y los botones quedaban a distinta altura. */}
                {indice === 0 ? <span className="etq etq-ok">Principal</span> : null}
              </div>

              <div className="galeria-admin-acciones">
                <button
                  type="button"
                  className="btn btn-sm btn-contorno"
                  disabled={ocupado || indice === 0}
                  aria-label={`Mover ${String(indice + 1)}° foto un lugar antes`}
                  onClick={() => {
                    mover(imagen.id, 'subir');
                  }}
                >
                  <Icono nombre="flechaIzq" />
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-contorno"
                  disabled={ocupado || indice === imagenes.length - 1}
                  aria-label={`Mover ${String(indice + 1)}° foto un lugar despues`}
                  onClick={() => {
                    mover(imagen.id, 'bajar');
                  }}
                >
                  <Icono nombre="flecha" />
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-peligro"
                  disabled={ocupado}
                  aria-label={`Quitar la ${String(indice + 1)}° foto`}
                  onClick={() => {
                    setPorEliminar(imagen);
                  }}
                >
                  <Icono nombre="cerrar" />
                </button>
              </div>

              {/* Las medidas son las del archivo ya convertido, no las del original. */}
              <p className="galeria-admin-medida">
                {imagen.ancho} × {imagen.alto}
              </p>

              <label className="solo-lectores" htmlFor={`alt-${String(imagen.id)}`}>
                Que se ve en la {indice + 1}° foto
              </label>
              {/* Area de texto y no `<input>`: el nombre del producto (el
                  marcador) suele ser largo y en una linea quedaba cortado. Enter
                  no agrega un salto —un alt es una frase—, termina la edicion. */}
              <textarea
                id={`alt-${String(imagen.id)}`}
                className="galeria-admin-alt"
                rows={3}
                autoComplete="off"
                placeholder={nombreProducto}
                defaultValue={imagen.textoAlternativo ?? ''}
                disabled={ocupado}
                onKeyDown={(evento) => {
                  if (evento.key !== 'Enter') return;
                  evento.preventDefault();
                  evento.currentTarget.blur();
                }}
                onBlur={(evento) => {
                  describir(imagen.id, evento.target.value);
                }}
              />
            </li>
          ))}
        </ul>
      ) : (
        <p className="panel-vacio">Este producto todavia no tiene fotos.</p>
      )}

      <SubidorImagen
        etiqueta="elige un archivo"
        ayuda="JPEG, PNG o WebP de hasta 12 MB. Se publica en WebP, con el lado mayor en 1920 px."
        subiendo={subida.subiendo}
        onArchivo={subir}
      />

      <Modal
        titulo="Quitar esta foto"
        bajada="No se puede deshacer"
        abierto={porEliminar !== null}
        onCerrar={() => {
          if (!eliminando) setPorEliminar(null);
        }}
      >
        <p className="parrafo-panel">
          La foto se borra del catalogo y del almacen. Si despues la queres de vuelta, hay que
          volver a subir el archivo.
        </p>

        <div className="modal-acciones">
          <button
            type="button"
            className="btn btn-contorno"
            disabled={eliminando}
            onClick={() => {
              setPorEliminar(null);
            }}
          >
            Cancelar
          </button>
          <button type="button" className="btn btn-peligro" disabled={eliminando} onClick={eliminar}>
            {eliminando ? 'Quitando…' : 'Quitar la foto'}
          </button>
        </div>
      </Modal>
    </PanelAdmin>
  );
}
